/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectFhirHighlightForMessage, selectFhirHighlightTop, setFhirHighlight } from '../Fhir/fhirSlice';
import { useFhirScopeIfExists, useMessageIndex } from './hooks/MessageContext';
import { Callout, CalloutItem } from './Callout';

const FHIR_CALLOUT_ENABLED = true;
const MAX_CALLOUT_LINES_ON_WIDGET_HOVER = 4;

type FhirCalloutEntry = {
  fhirWidgetId: string;
  rightPanelWidgetId: string | undefined;
  predefinedWidgetId: string | undefined;
  observationGroupId: string | undefined;
};

function getElementVerticalCenter(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return rect.top + rect.height / 2;
}

function isElementVisibleInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight && rect.height > 0;
}

export function collapseCalloutItemsForWidgetHover(
  items: CalloutItem[],
  hoveringOverWidget: boolean,
  maxCalloutLines = MAX_CALLOUT_LINES_ON_WIDGET_HOVER
) {
  if (!hoveringOverWidget || items.length <= maxCalloutLines) return items;

  const candidates = items.filter((item) => isElementVisibleInViewport(item.sourceElement));
  const itemsToScore = candidates.length ? candidates : items;

  const bestItem = itemsToScore.reduce((best, item) => {
    const bestDistance = Math.abs(
      getElementVerticalCenter(best.sourceElement) - getElementVerticalCenter(best.targetElement)
    );
    const itemDistance = Math.abs(
      getElementVerticalCenter(item.sourceElement) - getElementVerticalCenter(item.targetElement)
    );
    return itemDistance < bestDistance ? item : best;
  }, itemsToScore[0]!);

  return [bestItem];
}

export function FhirCallout({ xOffset, yOffset }: { xOffset?: number; yOffset?: number }) {
  const messageIndex = useMessageIndex();
  const fhirScope = useFhirScopeIfExists();
  const currentlyHighlighted = useAppSelector((state) => selectFhirHighlightForMessage(state, messageIndex));
  const currentlyHighlightedTop = useAppSelector(selectFhirHighlightTop);
  const dispatch = useAppDispatch();

  const { items, shouldClearHighlight } = useMemo(() => {
    if (!FHIR_CALLOUT_ENABLED) return { items: [] as CalloutItem[], shouldClearHighlight: false };
    if (!currentlyHighlighted) return { items: [] as CalloutItem[], shouldClearHighlight: false };

    const { fhirWidgetIds, rightPanelWidgetIds, callouts } = currentlyHighlighted;

    // If key ids are missing, mark to clear (but don't dispatch here).
    if (!fhirWidgetIds || !fhirWidgetIds[0] || !rightPanelWidgetIds?.length) {
      console.log('Missing fhirWidgetId or rightPanelWidgetId', currentlyHighlighted);
      return { items: [] as CalloutItem[], shouldClearHighlight: true };
    }

    let needsClear = false;

    const resolvedItems: CalloutItem[] = [];

    (callouts as FhirCalloutEntry[]).forEach((callout, index) => {
      const { fhirWidgetId, rightPanelWidgetId, predefinedWidgetId, observationGroupId } = callout;

      const rightPanelWidgetContainer =
        document.getElementById(rightPanelWidgetId!) ||
        document.querySelector(`.fhir-sidebar [data-fhir-predefinedwidget="${predefinedWidgetId}"]`) ||
        document.querySelector(`[data-fhir-predefinedwidget="${predefinedWidgetId}"]`);

      let rightPanelWidgetElement =
        (rightPanelWidgetContainer?.querySelector('.fhir-border') as HTMLElement | null | undefined) ||
        rightPanelWidgetContainer;

      if (!rightPanelWidgetElement) {
        console.log('Could not find rightPanelWidgetElement', rightPanelWidgetIds, predefinedWidgetId, messageIndex);
        needsClear = true;
        return;
      }

      const fhirWidgetElement =
        (fhirScope?.scopeRef.current?.querySelector(`[id="${fhirWidgetId}"]`) as HTMLElement | undefined) ||
        (document.getElementById(fhirWidgetId) as HTMLElement | undefined);

      const rightPanelWidgetObsElement = observationGroupId
        ? (rightPanelWidgetElement?.querySelector(`tr[data-observationgroupid="${observationGroupId}"]`) as
            | HTMLElement
            | undefined)
        : undefined;

      const isFhir = fhirWidgetElement?.tagName === 'FHIR';

      if (!fhirWidgetElement) {
        console.log('Could not find fhirWidgetElement', fhirWidgetId);
        needsClear = true;
        return;
      }

      resolvedItems.push({
        key: `${fhirWidgetId}:${rightPanelWidgetId || predefinedWidgetId || 'widget'}:${observationGroupId || index}`,
        sourceElement: fhirWidgetElement,
        targetElement: rightPanelWidgetObsElement || rightPanelWidgetElement,
        xOffset,
        yOffset: isFhir ? yOffset : -5.5,
        treatSourceAsInlineAction: !isFhir,
      });
    });

    return { items: resolvedItems, shouldClearHighlight: needsClear && resolvedItems.length === 0 };
  }, [currentlyHighlighted, messageIndex, fhirScope, xOffset, yOffset]);

  const calloutItems = useMemo(
    () => collapseCalloutItemsForWidgetHover(items, !!currentlyHighlighted?.hoveringOverWidget),
    [items, currentlyHighlighted?.hoveringOverWidget]
  );

  // Do the state update *after* render.
  useEffect(() => {
    if (shouldClearHighlight) {
      dispatch(setFhirHighlight(undefined));
    }
  }, [shouldClearHighlight, dispatch]);

  return (
    <Callout
      className="fhircallout"
      items={calloutItems}
      refreshEventName="fhirSidebarScroll"
      refreshKey={currentlyHighlightedTop}
      isCurved
      targetContainerSelector=".fhir-sidebar"
      onMissingElement={() => dispatch(setFhirHighlight(undefined))}
    />
  );
}
